import {
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent,
    type PointerEvent,
    type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { GripHorizontal, GripVertical, Loader2, MoreHorizontal, PanelBottom, PanelRight } from "lucide-react";

import { LAYOUT } from "../layout";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { SidebarContext } from "../../context/SidebarContext";
import { Modal } from "./modal";
import { splitForPhone } from "./dockOverflow";
import {
    placeDock,
    positionFromOffsets,
    useDockOrientation,
    useDockPosition,
    VERTICAL_HOME,
    type DockInsets,
    type Size,
} from "./dockPosition";

/**
 * The therapist's adopted tools, as a dock.
 *
 * It used to be a rail pinned to the right edge, with each tool drawn as an
 * icon over its own name. That worked at three tools and stopped working at
 * four: a vertical rail is narrow, so every label was truncated — "Genograma
 * y…" — and adding group headings to a column that narrow turned it into a
 * stack of clipped text down the side of the page.
 *
 * So it moved to the bottom and became a dock: a horizontal row of icons,
 * centred, the shape every operating system uses for exactly this. The name is
 * still there — it is the accessible name, and it appears above the icon on
 * hover and on focus — it just is not painted under all of them at once.
 *
 * Each group is captioned — Gestión, Sistémica — because a hairline alone
 * says two tools are not the other two without saying why. The caption is a
 * short form: a therapy's catalogue name is written to be unambiguous in a
 * filter ("Terapia Sistémica / Familiar"), which is four times the width of
 * the icons underneath it. Assistive technology still gets the full name from
 * the group's own label.
 *
 * Deliberately no macOS-style magnification. The design standards forbid
 * layout-affecting hover — a target that grows as the pointer nears it is a
 * Fitts's-law penalty and a vestibular trigger — so hovering changes the
 * background and nothing else.
 *
 * The bottom centre is only where it starts. A dock that always sits over the
 * same strip of the page sometimes sits over the one thing a therapist needs
 * to read, so it can be picked up by its grip and put anywhere; the place is
 * remembered on this device and shared by every page that shows the dock.
 * Double-clicking the grip — or Home on the keyboard — puts it back.
 *
 * It can also stand up. A row along the bottom covers the end of every line
 * of a long note; a column down the right edge covers only the margin. The
 * button beside the grip turns it, and a column starts on the right — the
 * left belongs to the sidebar — halfway down. Turning it back puts the row
 * back where rows start, at the bottom centre.
 *
 * Not on a phone. There is no room on a phone screen for a dock anywhere but
 * along the bottom, and a column of 44px buttons down the side of a 375px
 * screen covers the text it was moved off. So on a phone it is a row at the
 * bottom, with neither the grip nor the turn button — and whatever was chosen
 * on a wider screen is kept for when the window is wide again.
 *
 * Nor all the tools, past four. Five or six 44px buttons with their captions
 * fill a phone's width edge to edge and wrap onto a second row over the page,
 * so the first three stay and the fourth slot becomes "Otras", which opens
 * the rest in a sheet — grouped as the dock groups them.
 */

/** Pixels per arrow-key press on the grip; Shift moves further. */
const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_LARGE = 64;
const EDGE_MARGIN = 8;
/** The left sidebar's widths, as AppSidebar draws them. */
const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 88;
/** How far the pointer travels before a press on the grip becomes a drag. */
const DRAG_THRESHOLD = 3;

export interface ToolDockItem {
    id: string;
    /** Shown on hover and on focus, and always the accessible name. */
    label: string;
    icon: ReactNode;
    onSelect: () => void;
    /** Swaps the icon for a spinner — a tool that is opening something. */
    busy?: boolean;
    disabled?: boolean;
}

export interface ToolDockGroup {
    key: string;
    /** The group's full name, for assistive technology. */
    label: string;
    /** The caption painted over the icons. Falls back to `label`. */
    shortLabel?: string;
    items: ToolDockItem[];
}

interface Props {
    groups: ToolDockGroup[];
    /** Names the dock itself. */
    ariaLabel: string;
    /** Shown above the dock; a launch that failed, typically. */
    error?: string | null;
}

export default function ToolDock({
    groups,
    ariaLabel,
    error,
}: Props) {
    const { t } = useTranslation();
    // The same test the app layout uses to show the bottom navigation — up to
    // and including tablets — so the dock clears the bar exactly where the bar
    // is. Each page used to pass a phone-only flag, which left the dock
    // half-hidden behind the bar on a tablet.
    const { isSmallScreen: clearsBottomBar, isMobile: fixedInPlace } = useBreakpoint();
    // The sidebar owns the left edge wherever it shows — every screen without
    // the bottom bar — so the dock stops at it rather than sliding underneath.
    // It widens on hover as well as when pinned open, and the page beside it
    // moves over in both cases; the dock moves with the page. Read from the
    // context directly, not through useSidebar, so a dock outside the app
    // layout simply has no sidebar to clear.
    const sidebar = useContext(SidebarContext);
    const sidebarWidth =
        sidebar && !clearsBottomBar
            ? sidebar.isExpanded || sidebar.isHovered
                ? SIDEBAR_WIDTH_EXPANDED
                : SIDEBAR_WIDTH_COLLAPSED
            : 0;
    const rootRef = useRef<HTMLDivElement>(null);
    const [moreOpen, setMoreOpen] = useState(false);
    const [storedPosition, move, reset] = useDockPosition();
    const [storedOrientation, setOrientation] = useDockOrientation();
    const position = fixedInPlace ? null : storedPosition;
    const orientation = fixedInPlace ? "horizontal" : storedOrientation;
    const vertical = orientation === "vertical";
    const [dockSize, setDockSize] = useState<Size>({ width: 0, height: 0 });
    const [viewport, setViewport] = useState<Size>(readViewport);
    const drag = useRef<{
        startX: number;
        startY: number;
        offsetX: number;
        offsetY: number;
        moved: boolean;
    } | null>(null);

    const insets: DockInsets = {
        top: EDGE_MARGIN,
        right: EDGE_MARGIN,
        left: EDGE_MARGIN + sidebarWidth,
        // Clear of the mobile navigation, which owns the bottom edge.
        bottom: EDGE_MARGIN + (clearsBottomBar ? parseInt(LAYOUT.NAV_HEIGHT.BOTTOM_BAR, 10) : 0),
    };

    // A moved dock is placed from its own size, so it has to be measured — and
    // measured again when an error line appears above it or a tool is adopted.
    useLayoutEffect(() => {
        const element = rootRef.current;
        if (!element) return;
        const measure = () => {
            // The dock itself, without the padding that lifts it off the
            // bottom edge in its default place: once moved there is no padding,
            // and a size that changes as the drag starts makes it jump.
            const rect = element.getBoundingClientRect();
            const padding = parseFloat(getComputedStyle(element).paddingBottom) || 0;
            const width = rect.width;
            const height = rect.height - padding;
            setDockSize((previous) =>
                previous.width === width && previous.height === height
                    ? previous
                    : { width, height },
            );
        };
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
        // Turning the dock swaps its width for its height; measured before
        // paint, so it is never placed for a frame by the shape it had.
    }, [groups.length, orientation]);

    useEffect(() => {
        const onResize = () => setViewport(readViewport());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    if (groups.length === 0) return null;

    const { shown: visibleGroups, overflow } = fixedInPlace
        ? splitForPhone(groups)
        : { shown: groups, overflow: [] as ToolDockGroup[] };

    const currentOffsets = () => {
        const rect = rootRef.current?.getBoundingClientRect();
        return { left: rect?.left ?? 0, top: rect?.top ?? 0 };
    };

    const moveTo = (left: number, top: number, persist: boolean) => {
        move(positionFromOffsets(left, top, readViewport(), dockSize, insets), { persist });
    };

    const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        const { left, top } = currentOffsets();
        drag.current = {
            startX: event.clientX,
            startY: event.clientY,
            offsetX: event.clientX - left,
            offsetY: event.clientY - top,
            moved: false,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
        if (!drag.current) return;
        if (
            !drag.current.moved &&
            Math.abs(event.clientX - drag.current.startX) < DRAG_THRESHOLD &&
            Math.abs(event.clientY - drag.current.startY) < DRAG_THRESHOLD
        ) {
            return;
        }
        drag.current.moved = true;
        moveTo(
            event.clientX - drag.current.offsetX,
            event.clientY - drag.current.offsetY,
            false,
        );
    };

    const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
        const finished = drag.current;
        drag.current = null;
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        // Written once, where it was let go, rather than on every frame.
        if (finished?.moved) {
            moveTo(
                event.clientX - finished.offsetX,
                event.clientY - finished.offsetY,
                true,
            );
        }
    };

    /** Back to where this shape of dock starts. */
    const goHome = () => {
        if (vertical) move(VERTICAL_HOME);
        else reset();
    };

    const toggleOrientation = () => {
        if (vertical) {
            setOrientation("horizontal");
            reset();
        } else {
            setOrientation("vertical");
            move(VERTICAL_HOME);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === "Home") {
            event.preventDefault();
            goHome();
            return;
        }
        const step = event.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;
        const delta: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0],
            ArrowRight: [step, 0],
            ArrowUp: [0, -step],
            ArrowDown: [0, step],
        };
        const shift = delta[event.key];
        if (!shift) return;
        event.preventDefault();
        const { left, top } = currentOffsets();
        moveTo(left + shift[0], top + shift[1], true);
    };

    const placement = position ? placeDock(position, viewport, dockSize, insets) : null;
    // A label goes where there is room for it. In a row, above the icon —
    // or below it in the upper half of the screen, where above runs off the
    // top. In a column, to the side facing the middle of the screen.
    const labelSide: LabelSide = vertical
        ? position !== null && position.x < 0.5
            ? "right"
            : "left"
        : position !== null && position.y < 0.5
          ? "below"
          : "above";

    return (
        <div
            ref={rootRef}
            data-testid="tool-dock"
            className={
                // Shrink-wrapped in both places, so the box that is measured
                // and dragged is the dock and nothing else. A full-width box
                // made the first grab measure the whole screen, and the dock
                // leapt away from the pointer; one that was not shrink-wrapped
                // wrapped onto two rows near the right edge, changed size
                // under the pointer and shook.
                placement
                    ? "pointer-events-none fixed z-40 flex w-max max-w-[100vw] select-none flex-col items-center gap-2"
                    : "pointer-events-none fixed bottom-0 left-1/2 z-40 flex w-max max-w-[100vw] -translate-x-1/2 select-none flex-col items-center gap-2"
            }
            style={
                placement
                    ? { left: placement.left, top: placement.top }
                    : {
                          // Centred on the page beside the sidebar, not on
                          // the window, so a wide dock does not start under it.
                          left: `calc(${sidebarWidth}px + (100vw - ${sidebarWidth}px) / 2)`,
                          // Clear of the mobile navigation, which owns the
                          // bottom edge, and of the notch inset on the
                          // devices that have one.
                          paddingBottom: clearsBottomBar
                              ? `calc(${LAYOUT.NAV_HEIGHT.BOTTOM_BAR} + ${LAYOUT.SAFE_AREA_PADDING})`
                              : `calc(12px + ${LAYOUT.SAFE_AREA_PADDING})`,
                      }
            }
        >
            {error && (
                <p
                    role="alert"
                    className="pointer-events-auto max-w-xs rounded-lg border border-status-critical/40 bg-surface-overlay px-3 py-1.5 text-center text-xs font-medium text-status-critical shadow-theme-md"
                >
                    {error}
                </p>
            )}

            {/* Wraps rather than scrolls.
                A scroll container clips whatever leaves its box, and what
                leaves this one is the label above each icon — so the tooltip
                was drawn and immediately cut in half. Wrapping costs a second
                row on a narrow phone with many tools, which is the case it was
                guarding against anyway. */}
            <nav
                aria-label={ariaLabel}
                className={`pointer-events-auto flex justify-center gap-1 rounded-2xl border border-white/15 bg-brand-600/90 shadow-theme-lg backdrop-blur-md ${
                    vertical
                        ? "flex-col items-center px-1.5 py-2"
                        : "max-w-[calc(100vw-1.5rem)] flex-wrap items-end px-2 py-1.5"
                }`}
            >
                {!fixedInPlace && (
                    <>
                        {/* The grip. A button so a keyboard can reach it: arrows move
                            the dock, Home puts it back. `touch-none` so dragging it on
                            a tablet moves the dock rather than scrolling the page. */}
                        <button
                            type="button"
                            aria-label={t("toolDock.move")}
                            title={t("toolDock.moveHint")}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                            onDoubleClick={goHome}
                            onKeyDown={handleKeyDown}
                            className={`flex shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-white/70 transition-colors duration-fast hover:bg-white/15 hover:text-white active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                                vertical ? "h-6 w-11" : "h-11 w-6 self-end"
                            }`}
                        >
                            {vertical ? (
                                <GripHorizontal className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <GripVertical className="h-4 w-4" aria-hidden="true" />
                            )}
                        </button>

                        {/* Turns the dock. Its icon is the shape it turns into: a
                            panel on the right, or one along the bottom. */}
                        <button
                            type="button"
                            aria-label={t(vertical ? "toolDock.horizontal" : "toolDock.vertical")}
                            title={t(vertical ? "toolDock.horizontal" : "toolDock.vertical")}
                            onClick={toggleOrientation}
                            className={`flex shrink-0 items-center justify-center rounded-lg text-white/70 transition-colors duration-fast hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                                vertical ? "h-8 w-11" : "h-11 w-8 self-end"
                            }`}
                        >
                            {vertical ? (
                                <PanelBottom className="h-4 w-4" aria-hidden="true" />
                            ) : (
                                <PanelRight className="h-4 w-4" aria-hidden="true" />
                            )}
                        </button>
                    </>
                )}

                {visibleGroups.map((group, index) => (
                    <div
                        key={group.key}
                        role="group"
                        aria-label={group.label}
                        className={vertical ? "flex flex-col items-center gap-1" : "flex items-end gap-1"}
                    >
                        {/* A hairline only where there is a boundary to mark:
                            one group of tools is not two. */}
                        {index > 0 && (
                            <span
                                aria-hidden="true"
                                className={
                                    vertical
                                        ? "my-1 h-px w-11 shrink-0 bg-white/25"
                                        : "mx-1 h-11 w-px shrink-0 self-end bg-white/25"
                                }
                            />
                        )}
                        <div className="flex flex-col items-center gap-0.5">
                            {visibleGroups.length > 1 && (
                                <span className="max-w-full truncate px-1 text-xs font-semibold uppercase tracking-[0.08em] text-white/70">
                                    {group.shortLabel ?? group.label}
                                </span>
                            )}
                            <div
                                className={
                                    vertical
                                        ? "flex flex-col items-center gap-1"
                                        : "flex items-center gap-1"
                                }
                            >
                                {group.items.map((item) => (
                                    <DockButton
                                        key={item.id}
                                        item={item}
                                        clearsCaption={visibleGroups.length > 1}
                                        labelSide={labelSide}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                ))}

                {overflow.length > 0 && (
                    <div className="flex items-end gap-1">
                        <span aria-hidden="true" className="mx-1 h-11 w-px shrink-0 self-end bg-white/25" />
                        <div className="flex flex-col items-center gap-0.5">
                            {visibleGroups.length > 1 && (
                                <span className="max-w-full truncate px-1 text-xs font-semibold uppercase tracking-[0.08em] text-white/70">
                                    {t("toolDock.more")}
                                </span>
                            )}
                            <DockButton
                                item={{
                                    id: "more",
                                    label: t("toolDock.moreTools"),
                                    icon: <MoreHorizontal className="h-5 w-5" />,
                                    onSelect: () => setMoreOpen(true),
                                }}
                                clearsCaption={visibleGroups.length > 1}
                                labelSide={labelSide}
                            />
                        </div>
                    </div>
                )}
            </nav>

            {overflow.length > 0 && (
                <Modal
                    isOpen={moreOpen}
                    onClose={() => setMoreOpen(false)}
                    showCloseButton={false}
                    className="mx-4 max-w-lg rounded-3xl"
                >
                    <div className="flex max-h-[80vh] flex-col overflow-y-auto p-5" data-testid="tool-dock-more">
                        <h2 className="mb-4 text-lg font-bold text-gray-800 dark:text-white">{t("toolDock.moreTools")}</h2>
                        {overflow.map((group) => (
                            <section key={group.key} aria-label={group.label} className="mb-2">
                                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-tertiary">
                                    {group.shortLabel ?? group.label}
                                </h3>
                                {group.items.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        disabled={item.disabled}
                                        onClick={() => {
                                            setMoreOpen(false);
                                            item.onSelect();
                                        }}
                                        className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50 p-3 text-left text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                                    >
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-brand-600 shadow-sm dark:bg-gray-800">
                                            {item.busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : item.icon}
                                        </span>
                                        <span className="flex-1 text-sm font-semibold">{item.label}</span>
                                    </button>
                                ))}
                            </section>
                        ))}
                        <button
                            type="button"
                            onClick={() => setMoreOpen(false)}
                            className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}

function readViewport(): Size {
    return { width: window.innerWidth, height: window.innerHeight };
}

type LabelSide = "above" | "below" | "left" | "right";

const LABEL_PLACEMENT: Record<Exclude<LabelSide, "above">, string> = {
    below: "left-1/2 top-full mt-2 -translate-x-1/2",
    left: "right-full top-1/2 mr-2 -translate-y-1/2",
    right: "left-full top-1/2 ml-2 -translate-y-1/2",
};

function DockButton({
    item,
    clearsCaption = false,
    labelSide = "above",
}: {
    item: ToolDockItem;
    /** Lifts a label drawn above the icon over the group caption, which sits in the same place. */
    clearsCaption?: boolean;
    /** Where the label goes, so it never runs off the screen's edge. */
    labelSide?: LabelSide;
}) {
    const [showing, setShowing] = useState(false);

    return (
        <span className="relative flex">
            {/* On focus as well as on hover: a label a keyboard never sees is
                not a label. `aria-hidden` because the button already carries
                the same words as its accessible name. */}
            {showing && (
                <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute z-10 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1 text-xs font-medium text-white shadow-theme-md dark:bg-gray-700 ${
                        labelSide === "above"
                            ? `bottom-full left-1/2 -translate-x-1/2 ${clearsCaption ? "mb-7" : "mb-2"}`
                            : LABEL_PLACEMENT[labelSide]
                    }`}
                >
                    {item.label}
                </span>
            )}

            <button
                type="button"
                onClick={item.onSelect}
                disabled={item.disabled}
                aria-label={item.label}
                onMouseEnter={() => setShowing(true)}
                onMouseLeave={() => setShowing(false)}
                onFocus={() => setShowing(true)}
                onBlur={() => setShowing(false)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white transition-colors duration-fast hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
                {item.busy ? (
                    <Loader2
                        className="h-5 w-5 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                    />
                ) : (
                    item.icon
                )}
            </button>
        </span>
    );
}
